import { generateSecretKey, getPublicKey } from "nostr-tools/pure";
import * as nip19 from "nostr-tools/nip19";
import { bytesToHex, hexToBytes } from "@noble/hashes/utils.js";

/** secp256k1 keypair. The same key signs capsules and receives private capsules. */
export type Keypair = {
  /** 32 bytes, never leaves the member's device unencrypted. */
  secretKey: Uint8Array;
  /** 32-byte x-only public key, lowercase hex. This is the member's address. */
  publicKey: string;
};

export function createKeypair(): Keypair {
  const secretKey = generateSecretKey();
  return { secretKey, publicKey: getPublicKey(secretKey) };
}

export function keypairFromSecret(secretKey: Uint8Array): Keypair {
  if (secretKey.length !== 32) throw new Error("secret key must be 32 bytes");
  return { secretKey, publicKey: getPublicKey(secretKey) };
}

export function secretKeyToHex(secretKey: Uint8Array): string {
  return bytesToHex(secretKey);
}

export function secretKeyFromHex(hex: string): Uint8Array {
  const bytes = hexToBytes(hex);
  if (bytes.length !== 32) throw new Error("secret key must be 32 bytes");
  return bytes;
}

/** Human-friendly form of a public key (bech32 "npub1..."), for display and sharing. */
export function publicKeyToNpub(publicKey: string): string {
  return nip19.npubEncode(publicKey);
}

export function npubToPublicKey(npub: string): string {
  const decoded = nip19.decode(npub);
  if (decoded.type !== "npub") throw new Error(`expected npub, got ${decoded.type}`);
  return decoded.data;
}

export function isValidPublicKey(publicKey: string): boolean {
  return /^[0-9a-f]{64}$/.test(publicKey);
}
